import type { QueryKey } from '@tanstack/react-query';

import type {
  BuildShipmentTargetRequest,
  GetAssignedShipmentQuickCaptureAvailabilityRequest,
  GetShipmentRequest,
  ListShipmentQuickCaptureTargetsRequest,
  ListShipmentWeighCaptureLinkTargetsRequest,
  ListShipmentWeighCaptureLinksRequest,
  ListShipmentCandidateLinesRequest,
  ListShipmentsRequest,
  ValidateShipmentRangeRequest,
} from '@crv/contracts/shipments';

export const shipmentsKeys = {
  resolveRcScope: (
    companyId: string,
    stationId: string,
    storeId?: string,
    locationId?: string,
  ): QueryKey => ['shipments', 'rcScope', companyId, stationId, storeId ?? '', locationId ?? ''],
  validateRange: (input: ValidateShipmentRangeRequest): QueryKey => [
    'shipments',
    'validateRange',
    input.companyId,
    input.rcScopeId,
    input.materialId,
    input.rangeStartAt,
    input.rangeEndAt,
  ],
  candidateLines: (input: ListShipmentCandidateLinesRequest): QueryKey => [
    'shipments',
    'candidateLines',
    input.companyId,
    input.rcScopeId,
    input.materialId,
    input.occurredAtStart,
    input.occurredAtEnd,
    input.presentationType ?? '',
    String(input.includeAlreadyShipped ?? false),
    String(input.limit ?? ''),
  ],
  buildTarget: (input: BuildShipmentTargetRequest): QueryKey => [
    'shipments',
    'buildTarget',
    input.companyId,
    input.rcScopeId,
    input.materialId,
    String(input.targetLb),
    String(input.operationalLossPct ?? ''),
  ],
  list: (input: ListShipmentsRequest): QueryKey => [
    'shipments',
    'list',
    input.companyId,
    input.rcScopeId ?? '',
    input.materialId ?? '',
    (input.statuses ?? []).join(','),
    input.from ?? '',
    input.to ?? '',
    String(input.includePendingNoReceiveDate ?? false),
  ],
  get: (input: GetShipmentRequest): QueryKey => [
    'shipments',
    'get',
    input.companyId,
    input.shipmentId,
  ],
  secureCaptureLinks: (input: ListShipmentWeighCaptureLinksRequest): QueryKey => [
    'shipments',
    'secureCaptureLinks',
    input.companyId,
    input.outboundRecordType ?? 'SHIPMENT',
    input.outboundRecordId ?? input.shipmentId ?? '',
    input.shipmentId ?? '',
  ],
  secureCaptureLinkTargets: (input: ListShipmentWeighCaptureLinkTargetsRequest): QueryKey => [
    'shipments',
    'secureCaptureLinkTargets',
    input.companyId,
  ],
  quickCaptureTargets: (input: ListShipmentQuickCaptureTargetsRequest): QueryKey => [
    'shipments',
    'quick-capture',
    input.companyId,
  ],
  quickCaptureAssignedAvailability: (
    input: GetAssignedShipmentQuickCaptureAvailabilityRequest,
  ): QueryKey => ['shipments', 'quick-capture', 'assigned-availability', input.companyId],
  evidenceSummary: (companyId: string, loadSessionId: string, groupId: string): QueryKey => [
    'shipments',
    'evidenceSummary',
    companyId,
    loadSessionId,
    groupId,
  ],
};
